import { useEffect, useState } from "react";
import { API } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

function Dot({ ok, warn }) {
  const color = ok ? "var(--mq-green)" : warn ? "var(--mq-yellow)" : "var(--mq-red)";
  return (
    <span
      className="mq-status-dot"
      style={{ background: color, boxShadow: `0 0 6px ${color}` }}
    />
  );
}

export default function PublicCompliance() {
  const { t, lang, setLang } = useI18n();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const r = await fetch(`${API}/public/compliance`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
      setErr("");
    } catch (e) {
      setErr(e.message);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 4000);
    return () => clearInterval(id);
  }, []);

  const c = data?.compliance || {};
  const rules = data?.rules || {};
  const spread = c.spread_pct ?? 0;
  const dBid = c.depth_bid_usd ?? 0;
  const dAsk = c.depth_ask_usd ?? 0;
  const vol = c.volume_24h_usd ?? 0;
  const volTarget = rules.volume_target_daily_usd ?? 1500;
  const volPct = c.volume_pct ?? 0;

  return (
    <div className="min-h-screen" style={{ background: "var(--mq-bg)" }}>
      <header
        className="sticky top-0 z-50 bg-black border-b"
        style={{ borderColor: "var(--mq-border)" }}
      >
        <div className="flex flex-wrap items-center justify-between px-4 sm:px-6 py-3 gap-4">
          <div className="flex items-center gap-3">
            <div className="w-2 h-2" style={{ background: "var(--mq-green)" }} />
            <span className="mq-heading text-lg font-bold tracking-tighter">
              MARQOLIX <span className="text-[var(--mq-text-2)]">NG</span>
            </span>
            <span className="mq-label">PUBLIC · COMPLIANCE PROOF</span>
          </div>
          <div className="flex items-center gap-1">
            <button
              data-testid="public-language-toggle-en"
              className={`mq-btn ${lang === "en" ? "mq-btn-primary" : ""}`}
              style={{ padding: "0.25rem 0.55rem", fontSize: "0.65rem" }}
              onClick={() => setLang("en")}
            >EN</button>
            <button
              data-testid="public-language-toggle-fr"
              className={`mq-btn ${lang === "fr" ? "mq-btn-primary" : ""}`}
              style={{ padding: "0.25rem 0.55rem", fontSize: "0.65rem" }}
              onClick={() => setLang("fr")}
            >FR</button>
          </div>
        </div>
      </header>

      <div className="px-4 sm:px-8 py-10 max-w-5xl mx-auto" data-testid="public-compliance-page">
        <div className="mb-8">
          <div className="mq-label mb-2">{t("compliance")} — NG v1.1</div>
          <h1 className="mq-heading text-3xl sm:text-5xl font-bold tracking-tighter mb-3">
            {data?.symbol || "—"} · <span className="text-[var(--mq-text-2)]">{(data?.exchange || "").toUpperCase()}</span>
          </h1>
          <div className="flex flex-wrap items-center gap-3 text-xs">
            <span className="mq-label" style={{ color: data?.running ? "var(--mq-green)" : "var(--mq-text-2)" }}>
              {data?.running ? "● LIVE" : "○ OFFLINE"}
            </span>
            {data?.testnet && (
              <span className="mq-label px-2 py-0.5 border" style={{ borderColor: "var(--mq-yellow)", color: "var(--mq-yellow)" }}>
                TESTNET
              </span>
            )}
            <span className="text-[var(--mq-text-3)]">
              {data?.ts_iso ? new Date(data.ts_iso).toLocaleString() : "—"}
            </span>
          </div>
        </div>

        {err && (
          <div className="mb-6 text-xs text-[var(--mq-red)]">⚠ {err}</div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="mq-panel p-5" data-testid="public-rule-spread">
            <div className="flex items-center justify-between mb-3">
              <Dot ok={c.spread_ok} warn={false} />
              <span className="mq-label" style={{ color: c.spread_ok ? "var(--mq-green)" : "var(--mq-red)" }}>
                {c.spread_ok ? t("pass") : t("fail")}
              </span>
            </div>
            <div className="mq-label mb-1">Rule 1</div>
            <div className="text-sm mb-3">{t("rule1")}</div>
            <div className="mq-metric text-3xl">{spread.toFixed(3)}%</div>
            <div className="mq-label mt-2">cap {(rules.spread_hard_cap_pct ?? 1.4).toFixed(2)}%</div>
          </div>

          <div className="mq-panel p-5" data-testid="public-rule-depth">
            <div className="flex items-center justify-between mb-3">
              <Dot ok={c.depth_ok} warn={false} />
              <span className="mq-label" style={{ color: c.depth_ok ? "var(--mq-green)" : "var(--mq-red)" }}>
                {c.depth_ok ? t("pass") : t("fail")}
              </span>
            </div>
            <div className="mq-label mb-1">Rule 2</div>
            <div className="text-sm mb-3">{t("rule2")}</div>
            <div className="mq-metric text-3xl">${Math.round(dBid)} / ${Math.round(dAsk)}</div>
            <div className="mq-label mt-2">bid / ask · ±{(rules.depth_band_pct ?? 2).toFixed(1)}% band · min ${Math.round(rules.min_depth_each_side_usd ?? 200)}</div>
          </div>

          <div className="mq-panel p-5" data-testid="public-rule-volume">
            <div className="flex items-center justify-between mb-3">
              <Dot ok={c.volume_ok} warn={!c.volume_ok && volPct >= 0.7} />
              <span
                className="mq-label"
                style={{
                  color: c.volume_ok
                    ? "var(--mq-green)"
                    : volPct >= 0.7
                      ? "var(--mq-yellow)"
                      : "var(--mq-red)",
                }}
              >
                {c.volume_ok ? t("pass") : volPct >= 0.7 ? t("warn") : t("fail")}
              </span>
            </div>
            <div className="mq-label mb-1">Rule 3</div>
            <div className="text-sm mb-3">{t("rule3")}</div>
            <div className="mq-metric text-3xl">${vol.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
            <div className="mq-label mt-2">
              target ${volTarget.toLocaleString()} · {(volPct * 100).toFixed(0)}%
            </div>
            <div className="mt-3 h-1 bg-[var(--mq-surface-hover)] relative">
              <div
                className="absolute top-0 left-0 bottom-0"
                style={{
                  width: `${Math.min(100, volPct * 100)}%`,
                  background: c.volume_ok
                    ? "var(--mq-green)"
                    : volPct >= 0.7
                      ? "var(--mq-yellow)"
                      : "var(--mq-red)",
                }}
              />
            </div>
          </div>
        </div>

        <div className="mq-panel p-5 text-xs text-[var(--mq-text-2)]">
          <div className="mq-label mb-2">DISCLAIMER</div>
          <p className="leading-relaxed">
            This page is generated live from the Marqolix NG v1.1 operator backend.
            It is intended for exchange market-maker compliance reviewers and audit purposes
            only. Values refresh every 4 seconds. No authentication is required; no order-book
            credentials, wallet balances, or strategy parameters are exposed.
          </p>
        </div>

        <div className="text-center text-[10px] text-[var(--mq-text-3)] tracking-widest uppercase py-8">
          Marqolix NG · v1.1.0 · {(data?.exchange || "").toUpperCase()} · {data?.testnet ? "TESTNET" : "LIVE"}
        </div>
      </div>
    </div>
  );
}
